import React, { useState, ChangeEvent, useRef } from 'react';
import DOMPurify from 'dompurify';
import './Conversation.css';
import '../Core/DashboardFormGrid.css';
import { buildCoreAssetUrl } from '../../config/environment';
import { submitTicketConversation } from '../../services/ticketService';

import { EditorState, convertToRaw, CompositeDecorator } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { Ticket, TicketConversation } from '../CommandCentre/utils/commandCenterTypes';

interface FileItem {
  file: File;
  preview: string;
}

type Recipient = {
  id: number;
  type: 'user' | 'instance';
  email: string;
  display_name?: string;
};

// Insert a custom "EMAIL" entity at the current cursor
const emailDecorator = new CompositeDecorator([
  {
    strategy: (contentBlock, callback, contentState) => {
      contentBlock.findEntityRanges(
        (character) => {
          const entityKey = character.getEntity();
          return entityKey !== null && contentState.getEntity(entityKey).getType() === 'EMAIL';
        },
        callback
      );
    },
    component: (props) => (
      <a
        href={`mailto:${props.contentState.getEntity(props.entityKey).getData().email}`}
        className="rdw-mention-link"
        style={{
          backgroundColor: '#d0e6ff',
          padding: '2px 6px',
          borderRadius: '4px',
          margin: '0 2px',
          display: 'inline-block',
          textDecoration: 'none',
          color: '#0366d6',
        }}
      >
        {props.children}
      </a>
    ),
  },
]);

const Conversation: React.FC<{
  commandCentreAuth: any;
  appLoading: any;
  conversationData: any;
  entityDisplayData: any;
  model: string;
  onTicketActivity?: (ticketId: number) => void;
  onTicketUpdated?: (ticket: Ticket) => void;
}> = ({ commandCentreAuth, appLoading, conversationData, entityDisplayData, model, onTicketActivity, onTicketUpdated }) => {
  const [suggestEditErrorMessage, setSuggestEditErrorMessage] = useState('');
  const currentAuthEntity = commandCentreAuth.entity;
  const [editorState, setEditorState] = useState(EditorState.createEmpty(emailDecorator));
  const [liveConversation, setLiveConversation] = useState<TicketConversation[]>([]);
  const [selectedRecipients, setSelectedRecipients] = useState<Recipient[]>([]);
  const [files, setFiles] = useState<FileItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const renderedConversation: TicketConversation[] = Array.isArray(conversationData?.conversation)
    ? conversationData.conversation
    : [];

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newFiles: FileItem[] = [...files];

    if (!e.target.files) return;

    for (let i = 0; i < e.target.files.length; i++) {
      const file = e.target.files[i];
      const reader = new FileReader();

      reader.onload = () => {
        const preview = reader.result as string;
        newFiles.push({ file, preview });
        setFiles([...newFiles]);
      };

      reader.readAsDataURL(file);
    }

    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles([...newFiles]);
  };

  const handleEditorChange = (state: EditorState) => {
    setEditorState(state);
  };

  const handleMentionSelect = (mention: any) => {

    const email = mention.email || mention.email_address || '';

    setSelectedRecipients((prev) => {
      const id = Number(mention.id);
      const type = (mention.type || 'user') as 'user' | 'instance';

      const exists = prev.some((r) => r.id === id && r.type === type);
      if (exists) return prev;

      const next: Recipient[] = [
        ...prev,
        {
          id,
          type,
          email,
          display_name: mention.text || mention.value,
        },
      ];
      return next;
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleConversationSubmission = () => {
    const rawContent = convertToRaw(editorState.getCurrentContent());
    const htmlContent = draftToHtml(rawContent);

    const formData = new FormData();
    formData.append('sender', JSON.stringify(currentAuthEntity));
    formData.append('conversation_text', htmlContent);
    formData.append('support_ticket', JSON.stringify(conversationData));
    formData.append('model', model);

    // Derive login_unique_id from URL (/dashboard/command-center/:slug)
    const currentPath = window.location.pathname || '';
    const slugMatch = currentPath.match(/\/dashboard\/(?:command-center|login)\/([^/]+)/i);
    const loginUniqueId = slugMatch ? slugMatch[1] : undefined;
    if (loginUniqueId) {
      formData.append('login_unique_id', loginUniqueId);
    }

    files.forEach(({ file }) => formData.append('files', file));

    // Include selected recipients
    formData.append('recipients', JSON.stringify(selectedRecipients));

    submitTicketConversation(formData)
      .then((res) => {
        // 🔔 Notify parent (CommandCenterContent) that this ticket has activity
        if (onTicketActivity && conversationData?.id) {
          onTicketActivity(conversationData.id);
        }
        if ((res as any)?.errorMessage) {
          setSuggestEditErrorMessage((res as any).errorMessage);
        } else {
          setEditorState(EditorState.createEmpty(emailDecorator));
          setFiles([]);
          setLiveConversation([...liveConversation, res]);
          setSelectedRecipients([]);
        }
      })
      .catch((e) => alert(e));
  };

  const handleSafeConversationSubmission = async () => {
    const rawContent = convertToRaw(editorState.getCurrentContent());
    const htmlContent = draftToHtml(rawContent);
    const plainTextContent = htmlContent.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

    if (!plainTextContent && files.length === 0) {
      setSuggestEditErrorMessage('Please enter a message or attach at least one file.');
      return;
    }

    setSuggestEditErrorMessage('');
    setSubmitting(true);

    const formData = new FormData();
    formData.append('sender', JSON.stringify(currentAuthEntity));
    formData.append('conversation_text', htmlContent);
    formData.append('support_ticket', JSON.stringify(conversationData));
    formData.append('model', model);

    const currentPath = window.location.pathname || '';
    const slugMatch = currentPath.match(/\/dashboard\/(?:command-center|login)\/([^/]+)/i);
    const loginUniqueId = slugMatch ? slugMatch[1] : undefined;
    if (loginUniqueId) {
      formData.append('login_unique_id', loginUniqueId);
    }

    files.forEach(({ file }) => formData.append('files', file));
    formData.append('recipients', JSON.stringify(selectedRecipients));

    try {
      const res = await submitTicketConversation(formData);
      if ((res as any)?.errorMessage) {
        setSuggestEditErrorMessage((res as any).errorMessage);
        return;
      }

      if (onTicketActivity && conversationData?.id) {
        onTicketActivity(conversationData.id);
      }

      setEditorState(EditorState.createEmpty(emailDecorator));
      setFiles([]);
      setSelectedRecipients([]);
      setLiveConversation((previous) => [...previous, res]);
      onTicketUpdated?.({
        ...conversationData,
        conversation: [...renderedConversation, ...liveConversation, res],
      });
    } catch (error) {
      setSuggestEditErrorMessage(
        error instanceof Error ? error.message : 'Failed to send the message.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const convertToAmPm = (militaryTime: string) => {
    const [hours, minutes, seconds] = militaryTime.split(':');
    const dateTime = new Date(2023, 0, 1, parseInt(hours), parseInt(minutes), parseInt(seconds));
    return dateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleClickFileInput = () => {
    fileInputRef.current?.click();
  };



  // ✅ Break down the conditions so nothing throws
  const isUser = currentAuthEntity?.type === 'user';
  const isInstance = currentAuthEntity?.type === 'instance';
  const isRegisteredInstance = commandCentreAuth?.rank === 'registered';

  // 👉 If you want to keep the original logic but safe:
  const canViewConversation =
    !!commandCentreAuth?.status &&
    !appLoading &&
    (
      // Internal / CC users (core-command staff) always allowed
      isUser ||
      // Instance dashboards: require registered or WM service
      (isInstance && (isRegisteredInstance))
    );  

  return (
    <>
      {canViewConversation && (
        <>
          {/* Conversation History */}
          <div className="website-details">
            <div className="core-command-service-details-inner">
              <div className="conversation-history">
                <div className="conversation-heading">
                  <h2>
                    <i className="fa fa-comment"></i> Conversation history
                  </h2>
                </div>

                {renderedConversation === undefined ||
                ((renderedConversation as any) === '[]' && liveConversation.length === 0) ? (
                  <h3 className="no-previous-messages">
                    <i className="fa fa-certificate"></i> There are no previous messages
                  </h3>
                ) : (
                  <>
                    {/* From DB */}
                    {renderedConversation.map((message: any, index: number) => {
                      const matchingProfile = entityDisplayData.find(
                        (p: any) => p.id === message.sender.id && p.type === message.sender.type
                      );
                      return (
                        <div
                          key={message.sender.id + index}
                          id={message.sender.id === currentAuthEntity.id ? 'me' : 'other'}
                          className={`message message-${index + 1}`}
                        >
                          <div className="message-inner">
                            <div className="message-author">
                              {matchingProfile?.profile_picture ? (
                                <img
                                  src={buildCoreAssetUrl(matchingProfile.profile_picture)}
                                  className="profile-picture"
                                  alt=""
                                />
                              ) : (
                                <div className="placeholder-image"></div>
                              )}
                              <span className="message-display-name">
                                {message.sender.id === currentAuthEntity.id ? 'Me' : matchingProfile?.display_name}
                              </span>
                              <div className="message-date-posted">
                                <span>
                                  <i className="fa fa-calendar" /> {message.date}
                                </span>
                                <span>
                                  <i className="fa fa-clock" /> {convertToAmPm(message.time_posted)}
                                </span>
                              </div>
                            </div>
                            <div
                              className="message-text"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(message.conversation_text || ''),
                              }}
                            />
                            {message.file_data?.length > 0 && (
                              <div className="attached-file-container">
                                <h3>
                                  <i className="fa fa-link"></i> Attached files
                                </h3>
                                {message.file_data.map((item: any, idx: number) => (
                                  <div key={idx} className="file-preview-item">
                                    {item.mimetype.startsWith('image/') ? (
                                      <img
                                        src={buildCoreAssetUrl(item.path)}
                                        className="file-preview-image"
                                        alt={item.originalname || 'Attachment preview'}
                                      />
                                    ) : (
                                      <div className="file-preview-placeholder">
                                        <i className="fa fa-file file-preview-icon" />
                                        <div className="file-name">{item.originalname}</div>
                                      </div>
                                    )}
                                    <a
                                      target="_blank"
                                      rel="noreferrer"
                                      href={buildCoreAssetUrl(item.path)}
                                      className="fa fa-download"
                                      aria-label={`Download ${item.originalname || 'attachment'}`}
                                    >
                                      <span className="sr-only">
                                        Download {item.originalname || 'attachment'}
                                      </span>
                                    </a>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Live Conversation */}
                    {liveConversation.map((message: any, index: number) => {
                      const matchingProfile = entityDisplayData.find((p: any) => p.id === message.sender.id);
                      return (
                        <div
                          key={message.id || `${message.sender?.id}-${index}`}
                          id={message.sender.id === currentAuthEntity.id ? 'me' : 'other'}
                          className={'message message-' + (index + 1)}
                        >
                          <div className="message-inner">
                            <span className="message-new-badge">NEW</span>
                            <div className="message-author">
                              {matchingProfile?.profile_picture ? (
                                <img
                                  src={buildCoreAssetUrl(matchingProfile.profile_picture)}
                                  className="profile-picture"
                                  alt=""
                                />
                              ) : (
                                <div className="placeholder-image"></div>
                              )}
                              <span className="message-display-name">
                                {message.sender.id === currentAuthEntity.id ? 'Me' : matchingProfile?.display_name}
                              </span>
                              <div className="message-date-posted">
                                <span>
                                  <i className="fa fa-calendar" /> {message.date}
                                </span>
                                <span>
                                  <i className="fa fa-clock" /> {convertToAmPm(message.time_posted)}
                                </span>
                              </div>
                            </div>
                            <div
                              className="message-text"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(message.conversation_text || ''),
                              }}
                            />
                            {message.file_data?.length > 0 && (
                              <div className="attached-file-container">
                                <h3>
                                  <i className="fa fa-link"></i> Attached files
                                </h3>
                                {message.file_data.map((item: any, idx: number) => (
                                  <div key={item.id || idx} className="file-preview-item">
                                    {item.mimetype.startsWith('image/') ? (
                                      <img
                                        src={buildCoreAssetUrl(item.path)}
                                        className="file-preview-image"
                                        alt={item.originalname || 'Attachment preview'}
                                      />
                                    ) : (
                                      <div className="file-preview-placeholder">
                                        <i className="fa fa-file file-preview-icon" />
                                        <div className="file-name">{item.originalname}</div>
                                      </div>
                                    )}
                                    <a
                                      target="_blank"
                                      rel="noreferrer"
                                      href={buildCoreAssetUrl(item.path)}
                                      className="fa fa-download"
                                      aria-label={`Download ${item.originalname || 'attachment'}`}
                                    >
                                      <span className="sr-only">
                                        Download {item.originalname || 'attachment'}
                                      </span>
                                    </a>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Message Input */}
          <div className="website-details" id="suggest-edits">
            <div className="core-command-service-details-inner">
              <h2>
                <i className="fa fa-pencil"></i> Write a message
              </h2>
              {suggestEditErrorMessage && <span className="error_message">{suggestEditErrorMessage}</span>}
              <Editor
                editorState={editorState}
                onEditorStateChange={handleEditorChange}
                placeholder="Enter your suggestions..."
                toolbar={{ options: ['inline', 'list', 'link'] }}
                mention={{
                  separator: ' ',
                  trigger: '@',
                  suggestions: entityDisplayData.map((entity: any) => ({
                    text: entity.display_name,
                    value: entity.display_name,
                    id: entity.id,
                    type: entity.type,
                    // 🔹 Prefer entity.email, fall back to entity.email_address
                    email: entity.email || entity.email_address,
                    url: '',
                  })),
                  onAddMention: handleMentionSelect,
                }}
              />

              {/* File Upload */}
              <div className="message-submission-container">
                <div className="file-upload-container">
                  <button onClick={handleClickFileInput}>
                    <i className="fa fa-file-code"></i> Attach files to message ({files.length})
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf, .doc, .docx, .png, .jpeg, .jpg"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
                <button
                  className="conversation-message-submission-btn confirm-customization-options"
                  onClick={handleSafeConversationSubmission}
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Send message'}
                </button>
              </div>

              {/* File Preview */}
              <div className="file-preview-container">
                {files.map((item, index) => (
                  <div key={index} className="file-preview-item">
                    <div className="file-preview-inner-item">
                      {item.file.type.includes('image') ? (
                        <img src={item.preview} alt={`preview-${index}`} className="file-preview-image" />
                      ) : (
                        <div className="file-preview-placeholder">
                          <i className="fa fa-file file-preview-icon" />
                          <div className="file-name">{item.file.name}</div>
                        </div>
                      )}
                      <span onClick={() => handleRemoveFile(index)} className="close-icon">
                        &#10006;
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Conversation;

